import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  Box,
  CheckCircle2,
  Clock,
  Layers,
  MoreHorizontal,
  Pencil,
  Plus,
  Printer,
  Search,
  Trash2,
  Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  useDeleteEngPrint,
  useEngPrints,
  useEngSignedUrl,
  type EngPrint,
  type EngProject,
} from '@/features/engineering/hooks';
import {
  ENG_PRINT_STATUS_OPTIONS,
  formatPrintTime,
  getEngPrintStatusMeta,
} from '@/features/engineering/model';
import { formatError } from '@/lib/errors';
import { formatCurrency, formatDateOnly } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { PersonChip, StatCard } from './EngParts';
import { EngPrintDialog } from './EngPrintDialog';
import { ModelViewerDialog, type ModelViewerTarget } from './ModelViewerDialog';
import { printViewerTarget, summarisePrints } from './engPrintUtils';

const ALL = '__all__';
const EMPTY: EngPrint[] = [];

export function PrintStatusBadge({ status, className }: { status: string; className?: string }) {
  const meta = getEngPrintStatusMeta(status);
  return (
    <Badge variant={meta.variant} className={cn('whitespace-nowrap', className)}>
      {meta.label}
    </Badge>
  );
}

/** Square photo thumbnail with a printer glyph fallback. */
export function PrintThumb({
  print,
  className,
  onClick,
}: {
  print: EngPrint;
  className?: string;
  onClick?: () => void;
}) {
  const { data: url } = useEngSignedUrl(print.photo_path);
  const inner = url ? (
    <img src={url} alt={`Photo of ${print.title}`} className="h-full w-full object-cover" loading="lazy" />
  ) : (
    <Printer className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
  );
  const classes = cn(
    'flex shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-secondary',
    className,
  );
  if (url && onClick) {
    return (
      <button type="button" className={cn(classes, 'cursor-zoom-in')} onClick={onClick} aria-label="Open photo">
        {inner}
      </button>
    );
  }
  return <div className={classes}>{inner}</div>;
}

export function PrintPhotoLightbox({ print, onClose }: { print: EngPrint | null; onClose: () => void }) {
  const { data: url } = useEngSignedUrl(print?.photo_path ?? null);
  return (
    <Dialog open={!!print} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl p-2 sm:p-3">
        <DialogTitle className="sr-only">{print?.title ?? 'Print photo'}</DialogTitle>
        {url ? (
          <img src={url} alt={print ? `Photo of ${print.title}` : ''} className="max-h-[80vh] w-full rounded-md object-contain" />
        ) : (
          <Skeleton className="h-[60vh] w-full" />
        )}
        {print && (
          <p className="px-1 pb-1 text-sm text-muted-foreground">
            {print.title}
            {print.printed_on ? ` · ${formatDateOnly(print.printed_on)}` : ''}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface EngPrintsTabProps {
  project: EngProject;
  /** Logging prints stays available while the design is locked — it's production feedback. */
  canEdit: boolean;
}

/**
 * Every print made for the project: what, on which printer, how it
 * went and what needs to change. The summary strip rolls up success
 * rate, time and material cost.
 */
export function EngPrintsTab({ project, canEdit }: EngPrintsTabProps) {
  const { data: prints, isLoading } = useEngPrints(project.id);
  const remove = useDeleteEngPrint();
  const [dialog, setDialog] = useState<{ print: EngPrint | null } | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>(ALL);
  const [query, setQuery] = useState('');
  const [lightbox, setLightbox] = useState<EngPrint | null>(null);
  const [viewer, setViewer] = useState<ModelViewerTarget | null>(null);

  const list = prints ?? EMPTY;
  const summary = useMemo(() => summarisePrints(list), [list]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return list.filter((p) => {
      if (statusFilter !== ALL && p.status !== statusFilter) return false;
      if (!q) return true;
      const hay = [
        p.title,
        p.file?.name,
        p.printer?.name,
        p.printer_name,
        p.material,
        p.color,
        p.notes,
        p.changes_needed,
        p.printed_by_profile?.full_name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [list, statusFilter, query]);

  const handleDelete = async (print: EngPrint) => {
    if (!window.confirm(`Delete the print “${print.title}”? This cannot be undone.`)) return;
    try {
      await remove.mutateAsync({ print });
      toast.success('Print deleted.');
    } catch (err) {
      toast.error(formatError(err, 'Could not delete the print.'));
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Prints logged"
          value={summary.total}
          icon={Printer}
          hint={summary.inProgress > 0 ? `${summary.inProgress} planned or printing` : 'Across all revisions'}
        />
        <StatCard
          label="Success rate"
          value={summary.successRate == null ? '—' : `${Math.round(summary.successRate * 100)}%`}
          icon={CheckCircle2}
          hint={
            summary.finished > 0
              ? `${summary.successes} of ${summary.finished} finished print${summary.finished === 1 ? '' : 's'}`
              : 'No finished prints yet'
          }
        />
        <StatCard
          label="Printer time"
          value={summary.totalMinutes > 0 ? formatPrintTime(summary.totalMinutes) : '—'}
          icon={Clock}
          hint={
            summary.total > 0 && summary.totalMinutes > 0
              ? `≈ ${formatPrintTime(Math.round(summary.totalMinutes / summary.total))} per print`
              : undefined
          }
        />
        <StatCard
          label="Spent on prints"
          value={formatCurrency(summary.totalCents)}
          icon={Wallet}
          hint={summary.totalGrams > 0 ? `${Math.round(summary.totalGrams).toLocaleString()} g of material` : 'Rolls into the budget'}
        />
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative sm:max-w-xs sm:flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search prints…"
              className="pl-8"
              aria-label="Search prints"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="sm:w-44" aria-label="Filter by status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All statuses</SelectItem>
              {ENG_PRINT_STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {canEdit && (
          <Button onClick={() => setDialog({ print: null })}>
            <Plus className="mr-2 h-4 w-4" />
            Log a print
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : list.length === 0 ? (
        <EmptyState
          icon={Printer}
          title="No prints logged yet"
          action={
            canEdit ? (
              <Button onClick={() => setDialog({ print: null })}>
                <Plus className="mr-2 h-4 w-4" />
                Log the first print
              </Button>
            ) : undefined
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState icon={Search} title="No prints match that filter" />
      ) : (
        <ul className="space-y-2">
          {filtered.map((print) => (
            <PrintRow
              key={print.id}
              print={print}
              canEdit={canEdit}
              onEdit={() => setDialog({ print })}
              onDelete={() => handleDelete(print)}
              onOpenPhoto={() => setLightbox(print)}
              onView3D={() => {
                const target = printViewerTarget(print);
                if (target) setViewer(target);
              }}
            />
          ))}
        </ul>
      )}

      {dialog && (
        <EngPrintDialog
          open
          onOpenChange={(o) => !o && setDialog(null)}
          projectId={project.id}
          print={dialog.print}
          canDelete={canEdit}
        />
      )}
      <PrintPhotoLightbox print={lightbox} onClose={() => setLightbox(null)} />
      {viewer && <ModelViewerDialog target={viewer} onClose={() => setViewer(null)} />}
    </div>
  );
}

function Chip({ icon: Icon, children, className }: { icon?: typeof Clock; children: React.ReactNode; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground', className)}>
      {Icon && <Icon className="h-3.5 w-3.5" strokeWidth={1.6} />}
      {children}
    </span>
  );
}

function PrintRow({
  print,
  canEdit,
  onEdit,
  onDelete,
  onOpenPhoto,
  onView3D,
}: {
  print: EngPrint;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onOpenPhoto: () => void;
  onView3D: () => void;
}) {
  const printerLabel = print.printer?.name ?? print.printer_name ?? null;
  const materialLabel = [print.material, print.color].filter(Boolean).join(' · ');
  const settings = [
    print.layer_height_mm != null ? `${print.layer_height_mm} mm layers` : null,
    print.infill_percent != null ? `${print.infill_percent}% infill` : null,
  ]
    .filter(Boolean)
    .join(', ');
  const viewable = !!printViewerTarget(print);

  return (
    <li className="rounded-lg border border-border bg-card p-3 shadow-[0_1px_2px_0_hsl(124_26%_14%/0.04)] sm:p-4">
      <div className="flex gap-3 sm:gap-4">
        <PrintThumb print={print} className="h-16 w-16 sm:h-20 sm:w-20" onClick={onOpenPhoto} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="truncate text-left text-sm font-medium text-foreground hover:underline"
                  onClick={onEdit}
                >
                  {print.title}
                </button>
                <PrintStatusBadge status={print.status} />
                {print.quantity > 1 && <Badge variant="secondary">×{print.quantity}</Badge>}
              </div>
              <p className="mt-0.5 truncate text-xs text-muted-foreground">
                {print.file ? (
                  <>
                    {print.file.name}
                    {print.version ? ` · v${print.version.version_no}` : ''}
                  </>
                ) : (
                  'No part file linked'
                )}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {viewable && (
                <Button variant="outline" size="sm" className="hidden sm:inline-flex" onClick={onView3D}>
                  <Box className="mr-2 h-4 w-4" />
                  View in 3D
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Print actions">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={onEdit}>
                    <Pencil className="mr-2 h-3.5 w-3.5" />
                    {canEdit ? 'Edit print' : 'View details'}
                  </DropdownMenuItem>
                  {viewable && (
                    <DropdownMenuItem onSelect={onView3D}>
                      <Box className="mr-2 h-3.5 w-3.5" />
                      View part in 3D
                    </DropdownMenuItem>
                  )}
                  {canEdit && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={onDelete}>
                        <Trash2 className="mr-2 h-3.5 w-3.5" />
                        Delete print
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
            {printerLabel && <Chip icon={Printer}>{printerLabel}</Chip>}
            {materialLabel && <Chip icon={Layers}>{materialLabel}</Chip>}
            {settings && <Chip>{settings}</Chip>}
            {print.print_time_minutes != null && <Chip icon={Clock}>{formatPrintTime(print.print_time_minutes)}</Chip>}
            {print.material_grams != null && <Chip>{print.material_grams} g</Chip>}
            {print.cost_cents != null && <Chip icon={Wallet}>{formatCurrency(print.cost_cents)}</Chip>}
          </div>

          {(print.notes || print.changes_needed) && (
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {print.notes && (
                <div className="rounded-md bg-secondary/60 px-3 py-2">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">How it went</p>
                  <p className="mt-0.5 whitespace-pre-wrap text-sm text-foreground">{print.notes}</p>
                </div>
              )}
              {print.changes_needed && (
                <div className="rounded-md border border-fresh-salmon/50 bg-fresh-salmon/15 px-3 py-2">
                  <p className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-fresh-salmon-dark">
                    <AlertTriangle className="h-3 w-3" />
                    Needs changes
                  </p>
                  <p className="mt-0.5 whitespace-pre-wrap text-sm text-foreground">{print.changes_needed}</p>
                </div>
              )}
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>{print.printed_on ? formatDateOnly(print.printed_on) : 'No date'}</span>
            <PersonChip person={print.printed_by_profile} size="xs" />
          </div>
        </div>
      </div>
    </li>
  );
}
